import { ChangeEvent, FocusEvent, forwardRef, KeyboardEvent, useCallback, useMemo, useState } from 'react'

import { Icon } from '../../../Icon'
import { Input, InputProps, InputRef } from '../../../Input'
import { getSelectTogglerIcon } from '../../../Select'
import { useComboBox } from '../../contexts/comboBoxContext'

export type ComboBoxInputProps = Pick<
  InputProps,
  | 'alignment'
  | 'autoCompleted'
  | 'autoFocus'
  | 'bordered'
  | 'className'
  | 'disabled'
  | 'error'
  | 'focused'
  | 'hidden'
  | 'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onChangeDebounced'
  | 'onFocus'
  | 'onKeyDown'
  | 'onKeyPress'
  | 'onMouseDown'
  | 'onPressEnter'
  | 'placeholder'
  | 'readOnly'
  | 'selectOnFocus'
  | 'size'
  | 'success'
  | 'suffix'
  | 'type'
>

export const ComboBoxInput = forwardRef<InputRef, ComboBoxInputProps>(
  (
    {
      alignment = 'left',
      autoCompleted,
      autoFocus,
      bordered,
      className,
      disabled,
      error,
      focused = false,
      hidden,
      id,
      name,
      onBlur,
      onChange,
      onChangeDebounced,
      onFocus,
      onKeyPress,
      onMouseDown,
      onPressEnter,
      placeholder,
      readOnly,
      selectOnFocus,
      size,
      success,
      type,
    },
    forwardedRef,
  ) => {
    const [isFocused, setIsFocused] = useState(focused)
    const {
      addValueToSelected,
      formatDisplayedValue,
      inputValue,
      isDropdownInsideClickedRef,
      isDropdownOpen,
      isItemListEmpty,
      isMaxItemsLimitReached,
      isMultiselect,
      setInputValue,
      setIsDropdownInsideClickedRef,
      setIsDropdownOpen,
    } = useComboBox()

    const togglerIcon = useMemo(
      () => getSelectTogglerIcon({ isOpen: isDropdownOpen, isFocused, withSearch: true, withSearchOnly: false }),
      [isDropdownOpen, isFocused],
    )

    const handleInputFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(event)

        if (!isItemListEmpty || !!inputValue) {
          setIsDropdownOpen(true)
        }
      },
      [inputValue, isItemListEmpty, onFocus, setIsDropdownOpen],
    )

    const handleInputBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        if (isDropdownInsideClickedRef.current) {
          setIsDropdownInsideClickedRef(false)
        } else {
          setIsDropdownOpen(false)

          if (!!inputValue && isItemListEmpty) {
            addValueToSelected()
          }
        }

        setIsFocused(false)
        onBlur?.(event)
      },
      [
        addValueToSelected,
        inputValue,
        isDropdownInsideClickedRef,
        isItemListEmpty,
        onBlur,
        setIsDropdownInsideClickedRef,
        setIsDropdownOpen,
      ],
    )

    const handleInputValueChange = useCallback(
      (value: string) => {
        setInputValue(value)
        if (value) {
          setIsDropdownOpen(true)
        } else if (!value && isItemListEmpty) {
          setIsDropdownOpen(false)
        }
      },
      [isItemListEmpty, setInputValue, setIsDropdownOpen],
    )

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = event?.target.value

        handleInputValueChange(value)
        onChange?.(event)
      },
      [onChange, handleInputValueChange],
    )

    const handlePressEnter = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (isItemListEmpty) {
          addValueToSelected()
        }
        onPressEnter?.(event)
      },
      [addValueToSelected, isItemListEmpty, onPressEnter],
    )

    const handleKeyPress = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (isMultiselect && event.key === ',') {
          event.preventDefault() // without this, onChange will be triggered and will set the state again
          addValueToSelected()
        }
        onKeyPress?.(event)
      },
      [addValueToSelected, isMultiselect, onKeyPress],
    )

    return (
      <Input
        alignment={alignment}
        autoComplete="off"
        autoCompleted={autoCompleted}
        autoFocus={autoFocus}
        bordered={bordered}
        className={className}
        disabled={disabled || isMaxItemsLimitReached}
        error={error}
        focused={isFocused}
        hidden={hidden}
        id={id}
        name={name}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onChangeDebounced={onChangeDebounced}
        onFocus={handleInputFocus}
        onKeyPress={handleKeyPress}
        onMouseDown={onMouseDown}
        onPressEnter={handlePressEnter}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={forwardedRef}
        selectLook={false}
        selectOnFocus={selectOnFocus}
        size={size}
        success={success}
        suffix={<Icon icon={togglerIcon} />}
        title={inputValue}
        truncate
        type={type}
        value={formatDisplayedValue?.(inputValue) || inputValue}
      />
    )
  },
)
